import {
  BookOpenIcon,
  ChatIcon,
  ClipboardListIcon,
  CodeIcon,
  CursorClickIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  HomeIcon,
  IdentificationIcon,
  KeyIcon,
  LocationMarkerIcon,
  MailIcon,
  MapIcon,
  TranslateIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import { useLanguagesFaq } from '../sharedFaqs';
import type { IndustryContent } from '../types';
import GuestLanguages from '../visuals/GuestLanguages';
import { CheckGlyph, tone } from '../../primitives';

const BRAND_BG = 'bg-[#0a6f96]';
const BRAND_NAME = 'Costa Verde Surf Lodge';

export function useTravelContent(): IndustryContent {
  const { t } = useLocale('bot-use-cases');
  const { t: tc } = useLocale('chatbots');
  const languagesFaq = useLanguagesFaq();

  return {
    key: 'travel',
    seoTitle: t(
      'Travel and Hospitality Chatbot: AI Agent for Hotels and Tours',
    ),
    seoDescription: t(
      'Answer guests in their own language, day and night. An AI agent for hotels, tours and rentals that handles booking questions, check-in details and local tips on your website, WhatsApp and the phone.',
    ),
    hero: {
      title: t('Hotel and travel questions, answered.'),
      accent: t('In every guest’s language, at any hour.'),
      lead: t(
        'Guests ask about rooms, transfers, check-in and the best beach nearby at all hours. Your agent answers from your own information, in their language, and books what it can.',
      ),
      description: t(
        'Example: a guest arriving late asks a surf lodge about check-in and bringing a surfboard on the airport shuttle, and the agent adds the shuttle to their booking.',
      ),
      surface: 'widget',
      brand: {
        name: BRAND_NAME,
        initial: 'C',
        kind: t('Surf lodge'),
        bg: BRAND_BG,
      },
      messages: [
        {
          from: 'visitor',
          text: t('Hi! We land at 11 p.m. Is late check-in OK?'),
          at: 600,
        },
        {
          from: 'agent',
          typingAt: 1100,
          at: 1900,
          content: t(
            'Of course, reception is open all night. I can also book the airport shuttle for you. It’s €15 per person.',
          ),
        },
        {
          from: 'visitor',
          text: tc('Can I bring a surfboard on the airport shuttle?'),
          at: 2900,
        },
        {
          from: 'agent',
          typingAt: 3300,
          at: 4100,
          content: tc(
            'Yes, boards travel free on our shuttle. I’ve added it to your booking.',
          ),
        },
        { from: 'agent', at: 4700, content: <ShuttleBooked /> },
      ],
      steps: [
        {
          icon: BookOpenIcon,
          title: t('Checked the late arrival policy'),
          via: tc('Knowledge'),
          at: 1400,
        },
        {
          icon: CodeIcon,
          title: t('Found the booking'),
          via: tc('Custom action'),
          at: 3500,
        },
        {
          icon: CodeIcon,
          title: t('Added the shuttle and the board'),
          via: tc('Custom action'),
          at: 4300,
        },
      ],
    },
    jobs: {
      title: t('Every question a front desk gets.'),
      tail: t('Without the queue.'),
      lead: t(
        'From the first enquiry to the morning of departure, your agent answers guests straight away, in their own language, from the information you give it.',
      ),
      items: [
        {
          icon: HomeIcon,
          title: t('Rooms and rates'),
          detail: t(
            'Explains room types, prices and what’s included, and links to your booking engine.',
          ),
        },
        {
          icon: KeyIcon,
          title: t('Check-in and check-out'),
          detail: t(
            'Explains times, late arrivals and luggage storage, any time of night.',
          ),
        },
        {
          icon: MapIcon,
          title: t('Transfers and tours'),
          detail: t(
            'Shares transfer and tour options, and books them in your own system with a custom action.',
          ),
        },
        {
          icon: LocationMarkerIcon,
          title: t('Local tips'),
          detail: t(
            'Recommends restaurants, beaches and day trips from your own guide.',
          ),
        },
        {
          icon: TranslateIcon,
          title: t('Any language'),
          detail: t(
            'Replies in each guest’s language, so you don’t need a translator on every shift.',
          ),
        },
        {
          icon: UserGroupIcon,
          title: t('Groups and events'),
          detail: t(
            'Collects dates, numbers and contact details, and emails them to your team.',
          ),
        },
      ],
    },
    workflow: {
      title: t('Everything your guests ask about,'),
      tail: t('in one place.'),
      learns: [
        { icon: GlobeAltIcon, label: t('Your website') },
        { icon: DocumentTextIcon, label: t('House rules and FAQs') },
        { icon: MapIcon, label: t('Your local guide') },
        { icon: ClipboardListIcon, label: t('Menus and price lists') },
      ],
      acts: [
        { icon: CodeIcon, label: t('Your booking system') },
        { icon: CursorClickIcon, label: t('Booking engine button') },
        { icon: IdentificationIcon, label: tc('Collect data') },
        { icon: MailIcon, label: tc('Email') },
      ],
      channels: [
        { icon: ChatIcon, label: tc('Website chat') },
        { icon: 'FaWhatsapp', label: 'WhatsApp' },
        { icon: 'FaLine', label: 'LINE' },
        { icon: 'FaFacebookMessenger', label: 'Messenger' },
        { icon: 'FaPhone', label: tc('Phone calls') },
      ],
    },
    spotlight: {
      title: t('One agent,'),
      tail: t('in every guest’s language.'),
      lead: t(
        'Guests write in the language they think in. Your agent replies in kind, from the same information, so a question in Japanese gets the same answer as one in English.',
      ),
      points: [
        t('More than 80 languages'),
        t('On WhatsApp, LINE, Messenger and your website'),
        t('Phone calls in the language you choose'),
        t('One set of knowledge behind every answer'),
      ],
      description: t(
        'Example: three guests asking the lodge questions in French, Japanese and German, each answered in their own language.',
      ),
      visual: <GuestLanguages brand={BRAND_BG} name={BRAND_NAME} />,
    },
    faqs: [
      {
        question: t('Can it take bookings?'),
        answer: t(
          'It can send guests to your booking engine with a button, and a custom action lets it check availability or make bookings in your own system.',
        ),
      },
      {
        question: t('Does it work on WhatsApp and LINE?'),
        answer: t(
          'Yes. Connect WhatsApp, LINE, Messenger or Telegram and the same agent answers there, with the same knowledge as on your website.',
        ),
      },
      {
        question: t('Can guests call it?'),
        answer: t(
          'Yes. Connect a Twilio phone number and the agent answers calls in the language you set, from the same knowledge as in chat. Phone calls are available on paid plans.',
        ),
      },
      {
        question: t('What if a guest asks something it doesn’t know?'),
        answer: t(
          'Your instructions decide. It can take the guest’s details and email your front desk, or share your phone number. Later you can read the conversation and teach it the right answer.',
        ),
      },
      languagesFaq,
    ],
    finalExchange: {
      question: t('Is there parking at the lodge?'),
      answer: t('Yes, free parking right next to reception.'),
    },
    finalTitle: t('Your agent could be welcoming guests by lunchtime.'),
  };
}

/** The shuttle the agent booked, as a card in the chat. */
function ShuttleBooked() {
  const { t } = useLocale('bot-use-cases');

  return (
    <div className='flex max-w-[85%] items-center gap-3 rounded-2xl bg-[#f6f6f9] px-3.5 py-2.5 ring-1 ring-black/[0.05] dark:bg-white/[0.05] dark:ring-white/[0.06]'>
      <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
        <CheckGlyph className='h-3.5 w-3.5' />
      </span>
      <span className='min-w-0'>
        <span
          className={clsxm(
            'block text-[13px] font-semibold leading-tight',
            tone.ink,
          )}
        >
          {t('Airport shuttle booked')}
        </span>
        <span className={clsxm('block text-xs', tone.quiet)}>
          {t('Friday at 11:30 p.m. · 2 guests · 1 board')}
        </span>
      </span>
    </div>
  );
}
